import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  clone,
  filter,
  find,
  get,
  head,
  includes,
  isEmpty,
  map,
  size,
  union,
  without,
  slice,
  uniqBy,
} from "lodash";
import { showError, showSuccess } from "../v2_actions/error";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { GalleryPhotograph } from "./GalleryPhotograph";
import LoadingSpinner from "components/LoadingSpinner";
import { PageLayout } from "./layout/PageLayout";
import { PhotographEditor } from "./PhotographEditor";
import { confirmModal } from "../v2_actions/ui";
import { galleryList, setMapPhotographList } from "../v2_actions/gallery";
import { getUserStoredCountry } from "../v2_actions/auth_country";
import { makeStyles, Dialog, DialogTitle, DialogContent, IconButton } from "@material-ui/core";
import { photographList, deleteSelectedPhotographs } from "../v2_actions/photograph";
import CloseIcon from "@material-ui/icons/Close";
import { briefGalleryList } from "v2_actions/brief_gallery";
import { galleryEventDropdownOptions, albumDropdownOptions } from "../v2_actions/dropdown_options";

const PHOTOS_PER_PAGE = 20;

const useStyles = makeStyles({
  page: {
    padding: "28px 24px 120px",
    background: "#ffffff",
    minHeight: "100%",
    maxWidth: 1060,
    fontFamily: '"Open Sans", system-ui, -apple-system, Segoe UI, sans-serif',
    color: "#1b1b1b",
    "& *, & *::before, & *::after": {
      boxSizing: "border-box",
    },
  },
  pageTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    color: "#0b3556",
    letterSpacing: "-0.015em",
  },
  pageSub: {
    margin: "4px 0 0",
    fontSize: 13.5,
    color: "#7a7a76",
  },
  pageHeader: {
    marginBottom: 22,
  },
  filterbar: {
    background: "#fff",
    border: "1px solid #e0e0db",
    borderRadius: 14,
    boxShadow: "0 1px 2px rgba(11,53,86,0.04)",
    padding: 6,
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 6,
    marginBottom: 14,
  },
  dd: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "4px 12px 5px",
    borderRadius: 10,
    cursor: "pointer",
    transition: "background .12s",
    "&:hover": {
      background: "#f8f8f4",
    },
    "& + &": {
      borderLeft: "1px solid #ececea",
    },
  },
  ddLabel: {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: 14,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#7a7a76",
    fontWeight: 600,
  },
  ddVal: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    color: "#0b3556",
  },
  ddSwatch: {
    width: 8,
    height: 8,
    borderRadius: 2,
    background: "#3a86d8",
    flexShrink: 0,
  },
  ddChev: {
    marginLeft: "auto",
    color: "#7a7a76",
    transition: "transform .15s",
    flexShrink: 0,
  },
  ddChevOpen: {
    transform: "rotate(180deg)",
    color: "#0b3556",
  },
  ddMenu: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "calc(100% + 6px)",
    zIndex: 30,
    background: "#fff",
    border: "1px solid #e0e0db",
    borderRadius: 12,
    boxShadow: "0 6px 18px -8px rgba(11,53,86,0.18), 0 1px 2px rgba(11,53,86,0.06)",
    padding: 6,
    maxHeight: 320,
    overflowY: "auto",
    display: "none",
  },
  ddMenuOpen: {
    display: "block",
  },
  ddItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 11px",
    borderRadius: 8,
    fontSize: 13.5,
    color: "#1b1b1b",
    cursor: "pointer",
    "&:hover": {
      background: "#f8f8f4",
    },
  },
  ddItemActive: {
    background: "#e7f0fa",
    color: "#0b3556",
    fontWeight: 600,
  },
  ddItemMeta: {
    marginLeft: "auto",
    fontSize: 11.5,
    color: "#7a7a76",
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  search: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: "1px solid #e0e0db",
    borderRadius: 10,
    padding: "0 12px",
    transition: "border-color .15s, box-shadow .15s",
    "&:focus-within": {
      borderColor: "#3a86d8",
      boxShadow: "0 0 0 4px #e7f0fa",
    },
  },
  searchIcon: {
    color: "#7a7a76",
    flexShrink: 0,
  },
  searchInput: {
    border: 0,
    outline: 0,
    background: "transparent",
    width: "100%",
    padding: "11px 0",
    fontSize: 13.5,
    color: "#1b1b1b",
    fontFamily: "inherit",
    "&::placeholder": {
      color: "#7a7a76",
    },
  },
  tbtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 10,
    background: "#fff",
    border: "1px solid #e0e0db",
    color: "#4a4a48",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: ".12s",
    position: "relative",
    fontFamily: "inherit",
    "&:hover": {
      color: "#0b3556",
      borderColor: "#7a7a76",
    },
  },
  tbtnBadge: {
    position: "absolute",
    top: -5,
    right: -5,
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    background: "#3a86d8",
    color: "#fff",
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    border: "2px solid #fff",
    fontVariantNumeric: "tabular-nums",
  },
  selbar: {
    display: "none",
    alignItems: "center",
    gap: 10,
    background: "#0b3556",
    color: "#fff",
    borderRadius: 12,
    padding: "10px 14px",
    marginBottom: 14,
    fontSize: 13.5,
  },
  selbarOn: {
    display: "flex",
  },
  selbarCount: {
    fontWeight: 700,
  },
  selbarSpacer: {
    flex: 1,
  },
  selbarBtn: {
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    height: 30,
    padding: "0 11px",
    borderRadius: 7,
    fontSize: 12.5,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: ".12s",
    border: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": {
      background: "rgba(255,255,255,0.22)",
    },
  },
  selbarBtnDanger: {
    "&:hover": {
      background: "#c23a3a",
    },
  },
  selbarClose: {
    background: "transparent",
    padding: 0,
    width: 22,
    height: 30,
    border: 0,
    color: "#fff",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 24,
  },
  emptyMessage: {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: "40px 20px",
    color: "#7a7a76",
    fontSize: 14,
  },
  loadmoreWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    paddingBottom: 60,
  },
  progress: {
    width: 240,
    height: 4,
    background: "#e0e0db",
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#3a86d8",
    borderRadius: 999,
    transition: "width .3s",
  },
  progressMeta: {
    fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
    fontSize: 11,
    letterSpacing: "0.06em",
    color: "#7a7a76",
    fontVariantNumeric: "tabular-nums",
    "& b": {
      color: "#0b3556",
      fontWeight: 700,
    },
  },
  btnLoad: {
    marginTop: 6,
    padding: "12px 22px",
    background: "#3a86d8",
    color: "#fff",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 9,
    transition: ".12s",
    border: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": {
      background: "#2f75c4",
    },
  },
  dialogTitle: {
    position: "relative",
    paddingRight: 50,
    fontWeight: "bold",
    color: "#000",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  photoDetail: {
    marginBottom: 15,
    padding: 10,
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  photoDetailLabel: {
    fontWeight: "bold",
    marginRight: 10,
  },
});

const ChevSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TagSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const TrashSvg = ({ size: s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const CloseSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const LoadMoreChevSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FilterDropdown = ({ label, value, options, onSelect, showSwatch, classes }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className={classes.dd} ref={ref} onClick={() => setOpen(!open)}>
      <div className={classes.ddLabel}>{label}</div>
      <div className={classes.ddVal}>
        {showSwatch && <div className={classes.ddSwatch} />}
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
        <div className={`${classes.ddChev} ${open ? classes.ddChevOpen : ""}`}>
          <ChevSvg />
        </div>
      </div>
      <div className={`${classes.ddMenu} ${open ? classes.ddMenuOpen : ""}`}>
        {map(options, (opt, idx) => (
          <div
            key={idx}
            className={`${classes.ddItem} ${opt.active ? classes.ddItemActive : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(opt.value);
              setOpen(false);
            }}
          >
            <span>{opt.label}</span>
            {opt.meta && <span className={classes.ddItemMeta}>{opt.meta}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const GalleryView = ({ match }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { gallery_id } = useParams();
  const searchParams = window.location.search;
  const urlParams = new URLSearchParams(searchParams);
  const isUpdated = urlParams.get("updated");
  const country = getUserStoredCountry();
  const gallery = useSelector(() => galleryList.getObject(gallery_id));
  const loadingGalleries = useSelector(() => galleryList.isLoading());
  const galleries = useSelector(() => galleryList.getVisibleObjects());
  const allGalleries = useSelector(() => briefGalleryList.getVisibleObjects());
  const allEvents = useSelector(() => galleryEventDropdownOptions.getVisibleObjects());
  const allAlbums = useSelector(() => albumDropdownOptions.getVisibleObjects());
  const [firstGalleryId, setFirstGalleryId] = useState(null);
  const [defaultGalleryUrl, setDefaultGalleryUrl] = useState(false);
  const [activePhotographId, setActivePhotographId] = useState(null);
  const [localMapPhotographId, setLocalMapPhotographId] = useState(null);
  const [selectedPhotographIds, setSelectedPhotographIds] = useState([]);
  const firstGallery = useSelector(() => galleryList.getObject(firstGalleryId));
  const galleryPhotographs = useSelector(() =>
    photographList.getVisibleObjects(),
  );
  const [editingPhotographId, setEditingPhotographId] = useState(null);
  const [photosToShow, setPhotosToShow] = useState(PHOTOS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [loadedGalleryId, setLoadedGalleryId] = useState(gallery_id);
  const [showSoldIcons, setShowSoldIcons] = useState(false);
  const [eventFilter, setEventFilter] = useState("all");
  const [albumFilter, setAlbumFilter] = useState("all");

  useEffect(() => {
    if (isUpdated) dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  }, [isUpdated]);

  useEffect(() => {
    dispatch(briefGalleryList.updateListFilter({ all: true }));
    dispatch(briefGalleryList.fetchListIfNeeded(true));
    dispatch(galleryEventDropdownOptions.fetchWithFilter({}));
    dispatch(albumDropdownOptions.fetchWithFilter({}));
  }, []);

  useEffect(() => {
    if (gallery) {
      setLocalMapPhotographId(get(gallery, "map_photograph"));
    }
  }, [get(gallery, "map_photograph")]);

  useEffect(() => {
    if (gallery_id && gallery_id !== ":id") {
      setLoadedGalleryId(null);
      setPhotosToShow(PHOTOS_PER_PAGE);
      dispatch(photographList.fetchGalleryPhotographs(gallery_id)).then(() => {
        setLoadedGalleryId(gallery_id);
      });
    }
  }, [isUpdated, gallery_id]);

  useEffect(() => {
    if (includes(gallery_id, ":id")) {
      const lastGalleryId = sessionStorage.getItem("lastViewedGalleryId");
      if (lastGalleryId) {
        history.replace(`/photographer/gallery/${lastGalleryId}/view`);
        setDefaultGalleryUrl(true);
      } else {
        dispatch(galleryList.fetchListIfNeeded());
        setFirstGalleryId(get(head(galleries), "id"));
        dispatch(galleryList.ensureObjectLoaded(gallery_id));
      }
    }
  }, [gallery_id, galleries, firstGallery]);

  useEffect(() => {
    if (!defaultGalleryUrl && !isEmpty(firstGallery)) {
      history.replace(`/photographer/gallery/${get(firstGallery, "id")}/view`);
      setDefaultGalleryUrl(true);
    }
  }, [firstGallery]);

  useEffect(() => {
    if (gallery_id && gallery_id !== ":id") {
      sessionStorage.setItem("lastViewedGalleryId", gallery_id);
    }
  }, [gallery_id]);

  const onDeletePhotograph = (photograph) => {
    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(photographList.fetchGalleryPhotographs(gallery_id));
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Removed", "Removed from the gallery");
      }
    };

    const onConfirmed = () => {
      const values = Object.assign({}, photograph, {
        available_to_purchase: false,
      });
      dispatch(photographList.saveObject(values)).then(onDeleted);
    };

    dispatch(
      confirmModal({
        text: "Are you sure you want to make this photograph unavailable to purchase?",
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const onDeletePhotographWithoutGallerySizeFileInfo = (photograph) => {
    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(photographList.fetchGalleryPhotographs(gallery_id));
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Removed", `Photo: ${photograph.photo_name}`);
      }
    };
  };

  const onSetAsMapPhotograph = (photograph) => {
    setLocalMapPhotographId(photograph.id);

    dispatch(setMapPhotographList.set({
      galleryRef: gallery_id,
      photographId: photograph.id,
    })).then((json) => {
      if (json && json.errors) {
        setLocalMapPhotographId(get(gallery, "map_photograph"));
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
      }
    });
  };

  const onStartEditPhotograph = (photograph) => {
    setEditingPhotographId(photograph.id);
  };

  const onStopEditPhotograph = () => {
    setEditingPhotographId(null);
  };

  const onSavePhotographPrice = (photograph, newPrice) => {
    const values = Object.assign({}, photograph, { price: newPrice });
    dispatch(photographList.saveObject(values)).then((json) => {
      if (json && json.errors) {
        showError("Error", JSON.stringify(json.errors));
      }
    });
  };

  const handleToggleSoldIcons = () => {
    setShowSoldIcons(!showSoldIcons);
  };

  const onActivatePhotograph = (photograph) => {
    if (photograph) {
      setActivePhotographId(photograph.id);
    } else {
      setActivePhotographId(null);
    }
  };

  const onSelectPhotograph = (photograph) => {
    setSelectedPhotographIds(union(selectedPhotographIds, [photograph.id]));
  };

  const onUnSelectPhotograph = (photograph) => {
    setSelectedPhotographIds(without(selectedPhotographIds, photograph.id));
  };

  const getIsSelected = (photograph) => {
    return includes(selectedPhotographIds, photograph.id);
  };

  const getIsActivated = (photograph) => {
    return activePhotographId === photograph.id || getIsSelected(photograph);
  };

  const renderEditPhotograph = () => {
    return (
      <PhotographEditor
        photograph_id={editingPhotographId}
        country_id={get(gallery, ["gallery_price", "country"])}
        onClose={onStopEditPhotograph}
      />
    );
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleLoadMore = () => {
    setPhotosToShow(photosToShow + PHOTOS_PER_PAGE);
  };

  const handleViewSelectedPhotos = () => {
    if (size(selectedPhotographIds) > 0) {
      setShowViewPopup(true);
    }
  };

  const handleCloseViewPopup = () => {
    setShowViewPopup(false);
  };

  const handleDeleteSelectedPhotos = () => {
    if (size(selectedPhotographIds) === 0) {
      return;
    }

    const onConfirmed = () => {
      deleteSelectedPhotographs({
        selectedPhotographIds,
        showError,
        dispatch,
        setSelectedPhotographIds,
        showSuccess,
        gallery_id,
        galleryPhotographs,
      });
    };

    dispatch(
      confirmModal({
        text: `Are you sure you want to make ${size(
          selectedPhotographIds,
        )} photograph${size(selectedPhotographIds) > 1 ? "s" : ""} unavailable for purchase?`,
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const handleSelectAll = () => {
    setSelectedPhotographIds(map(paginatedPhotographs, (p) => p.id));
  };

  const handleClearSelection = () => {
    setSelectedPhotographIds([]);
  };

  const handleGalleryChange = (galleryId) => {
    history.push(`/photographer/gallery/${galleryId}/view`);
  };

  const filteredPhotographs = searchTerm
    ? filter(galleryPhotographs, (photo) =>
        get(photo, "photo_name", "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : galleryPhotographs;

  const paginatedPhotographs = slice(filteredPhotographs, 0, photosToShow);
  const hasMorePhotos = size(filteredPhotographs) > photosToShow;
  const totalPhotos = size(filteredPhotographs);
  const shownPhotos = Math.min(photosToShow, totalPhotos);
  const remainingPhotos = totalPhotos - shownPhotos;
  const progressPercent = totalPhotos > 0 ? (shownPhotos / totalPhotos) * 100 : 0;

  const selectedEventId = (() => {
    if (eventFilter === "all") return null;
    const found = filter(allEvents, (e) => get(e, "name") === eventFilter)[0];
    return get(found, "id");
  })();

  const eventOptions = [
    { label: "All events", value: "all", active: eventFilter === "all" },
    ...map(allEvents, (e) => ({
      label: get(e, "name"),
      value: get(e, "name"),
      active: eventFilter === get(e, "name"),
    })),
  ];

  const filteredAlbums = eventFilter === "all"
    ? allAlbums
    : filter(allAlbums, (a) => get(a, "gallery_event") === selectedEventId);

  const albumOptions = [
    { label: "All albums", value: "all", active: albumFilter === "all" },
    ...map(filteredAlbums, (a) => ({
      label: get(a, "name"),
      value: get(a, "name"),
      active: albumFilter === get(a, "name"),
    })),
  ];

  const galleriesFilteredByEvent = eventFilter === "all"
    ? allGalleries
    : filter(allGalleries, (g) => get(g, "gallery_event_name") === eventFilter);

  const galleriesMatchingAlbum = albumFilter === "all"
    ? galleriesFilteredByEvent
    : filter(galleriesFilteredByEvent, (g) => get(g, "album_name") === albumFilter);

  const filteredGalleries = galleriesMatchingAlbum;

  const galleryOptions = map(filteredGalleries, (g) => ({
    label: get(g, "gallery_name"),
    value: get(g, "id"),
    active: get(g, "id") === gallery_id,
  }));

  const selectedCount = size(selectedPhotographIds);

  return (
    <PageLayout
      title={get(gallery, "gallery_name")}
      gallery={gallery}
      withRightSidebar={false}
      hideGalleryDropdown={true}
    >
      {editingPhotographId && renderEditPhotograph()}
      <Dialog open={showViewPopup} onClose={handleCloseViewPopup} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          <span>Selected Photo Details</span>
          <IconButton onClick={handleCloseViewPopup} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {map(selectedPhotographIds, (photoId) => {
            const photo = filter(galleryPhotographs, (p) => p.id === photoId)[0];
            if (!photo) return null;
            return (
              <div key={photoId} className={classes.photoDetail}>
                <div>
                  <span className={classes.photoDetailLabel}>ID:</span>
                  <span>{get(photo, "id")}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Photo Name:</span>
                  <span>{get(photo, "photo_name")}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Available to Purchase:</span>
                  <span>{get(photo, "available_to_purchase") ? "Yes" : "No"}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Price:</span>
                  <span>{get(photo, "price")}</span>
                </div>
                <div>
                  <span className={classes.photoDetailLabel}>Times Sold:</span>
                  <span>{get(photo, "purchased_trolley_photographs", []).length}</span>
                </div>
              </div>
            );
          })}
        </DialogContent>
      </Dialog>

      <div className={classes.page}>
        <div className={classes.pageHeader}>
          <h1 className={classes.pageTitle}>Photos</h1>
          <p className={classes.pageSub}>Manage the photos within a gallery. Pick the hero photo, delete photos, view and change info and sold photos.</p>
        </div>
        <div className={classes.filterbar}>
          <FilterDropdown
            label="Event"
            value={eventFilter === "all" ? "All events" : eventFilter}
            options={eventOptions}
            onSelect={(val) => { setEventFilter(val); setAlbumFilter("all"); }}
            classes={classes}
          />
          <FilterDropdown
            label="Album"
            value={albumFilter === "all" ? "All albums" : albumFilter}
            options={albumOptions}
            onSelect={setAlbumFilter}
            classes={classes}
          />
          <FilterDropdown
            label="Gallery"
            value={get(gallery, "gallery_name") || get(find(allGalleries, (g) => get(g, "id") === gallery_id), "gallery_name", "Select gallery")}
            options={galleryOptions}
            onSelect={handleGalleryChange}
            showSwatch
            classes={classes}
          />
        </div>

        <div className={classes.toolbar}>
          <label className={classes.search}>
            <span className={classes.searchIcon}><SearchSvg /></span>
            <input
              type="text"
              className={classes.searchInput}
              placeholder="Search by Photo Name"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </label>
          <button
            className={classes.tbtn}
            onClick={handleToggleSoldIcons}
            style={showSoldIcons ? { color: "#0b3556", borderColor: "#3a86d8", background: "#e7f0fa" } : {}}
          >
            <TagSvg />
            <span>Sold Photos</span>
          </button>
          <button className={classes.tbtn} onClick={handleDeleteSelectedPhotos}>
            <TrashSvg />
            <span>Delete</span>
            {selectedCount > 0 && (
              <span className={classes.tbtnBadge}>{selectedCount}</span>
            )}
          </button>
        </div>

        <div className={`${classes.selbar} ${selectedCount > 0 ? classes.selbarOn : ""}`}>
          <span className={classes.selbarCount}><b>{selectedCount}</b> selected</span>
          <button className={classes.selbarBtn} onClick={handleSelectAll}>Select all</button>
          <span className={classes.selbarSpacer} />
          <button className={`${classes.selbarBtn} ${classes.selbarBtnDanger}`} onClick={handleDeleteSelectedPhotos}>
            <TrashSvg size={13} />
            Delete
          </button>
          <button className={classes.selbarClose} onClick={handleClearSelection}>
            <CloseSvg />
          </button>
        </div>

        {(loadingGalleries || !loadedGalleryId) && <LoadingSpinner />}

        <div className={classes.grid}>
          {loadedGalleryId && !loadingGalleries && paginatedPhotographs && map(paginatedPhotographs, (photo, index) => (
            <GalleryPhotograph
              key={photo.id}
              getIsSelected={getIsSelected}
              getIsActivated={getIsActivated}
              onActivatePhotograph={onActivatePhotograph}
              onSelectPhotograph={onSelectPhotograph}
              onUnSelectPhotograph={onUnSelectPhotograph}
              onDeletePhotograph={onDeletePhotograph}
              onDeletePhotographWithoutGallerySizeFileInfo={onDeletePhotographWithoutGallerySizeFileInfo}
              onSetAsMapPhotograph={onSetAsMapPhotograph}
              onEditPhotograph={onStartEditPhotograph}
              onSavePrice={onSavePhotographPrice}
              isMapPhotograph={localMapPhotographId === photo.id}
              showSoldIcons={showSoldIcons}
              index={index}
              photo={photo}
              country_id={get(gallery, ["gallery_price", "country"])}
            />
          ))}
          {!loadingGalleries && get(gallery, "id") && size(galleryPhotographs) === 0 && (
            <div className={classes.emptyMessage}>There are no photographs in this gallery</div>
          )}
        </div>

        {!loadingGalleries && hasMorePhotos && (
          <div className={classes.loadmoreWrap}>
            <div className={classes.progress}>
              <div className={classes.progressFill} style={{ width: `${progressPercent}%` }} />
            </div>
            <div className={classes.progressMeta}>
              <b>{shownPhotos}</b> of <b>{totalPhotos}</b> photos · <span>{remainingPhotos}</span> remaining
            </div>
            <button className={classes.btnLoad} onClick={handleLoadMore}>
              Load more
              <LoadMoreChevSvg />
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
